import axios from "axios";
import { useEffect, useState } from "react";
import { FiClock, FiEdit, FiMoreVertical, FiTrash2 } from "react-icons/fi";
import { MdToggleOff, MdToggleOn } from "react-icons/md";
import Breadcrumb from "../../../components/AdminSideComponents/BreadCrumb";
import AdminSlotTableSkeleton from "../../../components/AdminSideComponents/slot/AdminSlotTableSkeleton";
import DayToggleModal from "../../../components/AdminSideComponents/slot/DayToggleModal";
import EditSlotModal from "../../../components/AdminSideComponents/slot/EditSlotModal";
import { useAdminAuth } from "../../../context/AdminAuthContext";
import { convertTo12Hour } from "../../../utils/formatDateTime";
import {
  confirmAction,
  showError,
  showSuccess,
} from "../../../utils/toastUtils";

const DAYS = [
  { label: "Sun", value: 0 },
  { label: "Mon", value: 1 },
  { label: "Tue", value: 2 },
  { label: "Wed", value: 3 },
  { label: "Thu", value: 4 },
  { label: "Fri", value: 5 },
  { label: "Sat", value: 6 },
];

const AdminSlotManagement = () => {
  const { admin, isAdmin, isManager } = useAdminAuth();

  const [branches, setBranches] = useState([]);
  const [branchLoading, setBranchLoading] = useState(false);
  const [selectedBranch, setSelectedBranch] = useState(null);

  const [slots, setSlots] = useState([]);
  const [loading, setLoading] = useState(false);
  const [openMenu, setOpenMenu] = useState(null);
  const [showModal, setShowModal] = useState(false);
  const [editingSlot, setEditingSlot] = useState(null);
  const [dayToggleModal, setDayToggleModal] = useState(null);

  const fetchBranches = async () => {
    setBranchLoading(true);
    try {
      const res = await axios.get(
        `${process.env.REACT_APP_API_URL}/branches/get-all`,
      );
      const list = res.data?.data || [];
      setBranches(list);
      if (list.length > 0) {
        setSelectedBranch(list[0]._id); // default 0th index
      }
    } catch {
      showError("Failed to fetch branches");
    } finally {
      setBranchLoading(false);
    }
  };

  const fetchSlots = async (branchId) => {
    if (!branchId) return;

    setLoading(true);
    try {
      const res = await axios.get(
        `${process.env.REACT_APP_API_URL}/slot-bookings/get-all`,
        { params: { branchId } },
      );
      setSlots(res.data?.data || []);
    } catch (err) {
      showError("Failed to fetch slots");
      setSlots([]);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    if (isAdmin) {
      fetchBranches();
    } else if (isManager && admin?.assignedBranch) {
      setSelectedBranch(admin.assignedBranch?._id);
    }
  }, [isAdmin, isManager, admin]);

  useEffect(() => {
    if (selectedBranch) {
      fetchSlots(selectedBranch);
    }
  }, [selectedBranch]);

  const toggleStatus = async (slot) => {
    const nextStatus = slot.status === "active" ? "inactive" : "active";
    const confirmed = await confirmAction({
      title: `Mark slot as ${nextStatus}?`,
      text: "This will affect future availability only.",
      confirmText: "Yes",
    });
    if (!confirmed) return;

    try {
      await axios.patch(
        `${process.env.REACT_APP_API_URL}/slot-bookings/status/${slot._id}`,
        { status: nextStatus, branchId: selectedBranch },
      );

      setSlots((prev) =>
        prev.map((s) =>
          s._id === slot._id ? { ...s, status: nextStatus } : s,
        ),
      );
      showSuccess(`Slot ${nextStatus}`);
    } catch (error) {
      showError(
        error?.response?.data?.message || "Failed to update slot status",
      );
    }
  };

  const deleteSlot = async (slot) => {
    const confirmed = await confirmAction({
      title: "Delete this slot?",
      text: "This will not affect existing bookings.",
      confirmText: "Delete",
    });
    if (!confirmed) return;

    try {
      await axios.delete(
        `${process.env.REACT_APP_API_URL}/slot-bookings/${slot._id}`,
        { data: { branchId: selectedBranch } },
      );
      setSlots((prev) => prev.filter((s) => s._id !== slot._id));
      showSuccess("Slot deleted");
    } catch {
      showError("Failed to delete slot");
    }
  };

  return (
    <div className="bg-gradient-to-br px-6 min-h-screen from-[#FFF7F3] to-gray-50 py-2">
      <Breadcrumb
        title1="Slots"
        title2="Slot Templates"
        showBtn
        buttonName="Add New"
        onBtnClick={() => {
          setEditingSlot(null);
          setShowModal(true);
        }}
      />

      {/* BRANCH TABS – SUPER ADMIN ONLY */}
      {isAdmin && (
        <div className="bg-white rounded-lg shadow-md border mb-4 p-2">
          {branchLoading ? (
            <div className="flex gap-2 overflow-x-auto no-scrollbar">
              {Array.from({ length: 4 }).map((_, i) => (
                <div
                  key={i}
                  className="h-10 w-32 bg-gray-200 rounded-lg animate-pulse"
                />
              ))}
            </div>
          ) : (
            <div className="flex gap-2 overflow-x-auto no-scrollbar">
              {branches.map((b) => (
                <button
                  key={b._id}
                  onClick={() => setSelectedBranch(b._id)}
                  className={`px-4 py-2 rounded-lg text-sm font-semibold transition-all ${
                    selectedBranch === b._id
                      ? "bg-gradient-to-r from-accentTextColor to-accentTextColor/80 text-white shadow"
                      : "bg-gray-100 text-gray-600 hover:bg-brandAccentColor/40"
                  }`}
                >
                  {b.branchName}
                </button>
              ))}
            </div>
          )}
        </div>
      )}

      {/* SLOT TABLE */}
      <div className="bg-white mt-4 rounded-lg shadow-lg overflow-hidden">
        {loading ? (
          <AdminSlotTableSkeleton rows={5} />
        ) : slots.length === 0 ? (
          <div className="flex flex-col items-center justify-center py-20 text-gray-400">
            <FiClock className="text-6xl mb-4 opacity-30" />
            <p className="text-lg font-medium">No slot templates found</p>
          </div>
        ) : (
          <div className="overflow-x-auto">
            <table className="w-full table-fixed border border-gray-200 text-sm">
              <thead className="bg-brandAccentColor text-xs uppercase">
                <tr>
                  <th className="p-3 w-16 text-center">#</th>
                  <th className="p-3 w-60 text-center">Time Slot</th>
                  <th className="p-3 w-[30rem] text-center">Active Days</th>
                  <th className="p-3 w-52 text-center">Status</th>
                  <th className="p-3 w-20 text-center">Action</th>
                </tr>
              </thead>

              <tbody>
                {slots.map((slot, idx) => (
                  <tr key={slot._id} className="hover:bg-orange-50">
                    <td className="px-6">{idx + 1}</td>

                    <td className="px-10">
                      {convertTo12Hour(slot.startTime)} –{" "}
                      {convertTo12Hour(slot.endTime)}
                    </td>

                    <td className="px-6">
                      <div className="grid grid-cols-7 gap-2">
                        {DAYS.map((day) => {
                          const isActive = slot.activeDays.includes(day.value);
                          return (
                            <button
                              key={day.value}
                              onClick={() =>
                                setDayToggleModal({
                                  slot,
                                  day: day.value,
                                  label: day.label,
                                  isActive,
                                  branchId: selectedBranch,
                                })
                              }
                              className={`py-2 rounded-lg text-xs font-medium ${
                                isActive
                                  ? "bg-brandAccentColor/60 text-accentTextColor"
                                  : "bg-gray-100 text-gray-400"
                              }`}
                            >
                              {day.label}
                            </button>
                          );
                        })}
                      </div>
                    </td>

                    <td className="text-center">
                      <button onClick={() => toggleStatus(slot)}>
                        {slot.status === "active" ? (
                          <MdToggleOn className="text-5xl text-green-500" />
                        ) : (
                          <MdToggleOff className="text-5xl text-gray-400" />
                        )}
                      </button>
                    </td>

                    <td className="px-6 py-5 text-center">
                      <div className="relative inline-block">
                        <button
                          onClick={() =>
                            setOpenMenu(openMenu === slot._id ? null : slot._id)
                          }
                          className="p-2 rounded-lg hover:bg-gray-200 transition-colors group-hover:bg-gray-100"
                        >
                          <FiMoreVertical className="text-gray-600" />
                        </button>
                        {openMenu === slot._id && (
                          <>
                            <div
                              className="fixed inset-0 z-10"
                              onClick={() => setOpenMenu(null)}
                            />
                            {(() => {
                              const openUp = idx >= slots.length - 2;

                              return (
                                <div
                                  className={`absolute right-0 w-48 bg-white rounded-xl shadow-2xl border border-gray-200 py-2 z-20 animate-in duration-200        ${
                                    openUp
                                      ? "bottom-full mb-2 slide-in-from-bottom-2"
                                      : "top-full mt-2 slide-in-from-top-2"
                                  }`}
                                >
                                  <button
                                    onClick={() => {
                                      setEditingSlot(slot);
                                      setShowModal(true);
                                      setOpenMenu(null);
                                    }}
                                    className="flex w-full items-center px-4 py-3 gap-3 hover:bg-orange-50 transition-colors group/item"
                                  >
                                    <FiEdit className="text-[#f0653a] group-hover/item:scale-110 transition-transform" />
                                    <span className="text-gray-700 font-medium">
                                      Edit Slot
                                    </span>
                                  </button>

                                  <button
                                    onClick={() => {
                                      deleteSlot(slot);
                                      setOpenMenu(null);
                                    }}
                                    className="flex w-full items-center px-4 py-3 gap-3 hover:bg-red-50 transition-colors group/item"
                                  >
                                    <FiTrash2 className="text-red-600 group-hover/item:scale-110 transition-transform" />
                                    <span className="text-red-600 font-medium">
                                      Delete
                                    </span>
                                  </button>
                                </div>
                              );
                            })()}
                          </>
                        )}
                      </div>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </div>

      {showModal && (
        <EditSlotModal
          slot={editingSlot}
          branchId={selectedBranch}
          onClose={() => setShowModal(false)}
          onSuccess={() => {
            fetchSlots(selectedBranch);
            setShowModal(false);
          }}
        />
      )}

      {dayToggleModal && (
        <DayToggleModal
          dayToggleModal={dayToggleModal}
          fetchSlots={() => fetchSlots(selectedBranch)}
          setDayToggleModal={setDayToggleModal}
        />
      )}
    </div>
  );
};

export default AdminSlotManagement;

// import axios from "axios";
// import { useEffect, useState } from "react";
// import { FiClock, FiEdit, FiMoreVertical, FiTrash2 } from "react-icons/fi";
// import { MdToggleOff, MdToggleOn } from "react-icons/md";
// import Breadcrumb from "../../../components/AdminSideComponents/BreadCrumb";
// import AdminSlotTableSkeleton from "../../../components/AdminSideComponents/slot/AdminSlotTableSkeleton";
// import DayToggleModal from "../../../components/AdminSideComponents/slot/DayToggleModal";
// import EditSlotModal from "../../../components/AdminSideComponents/slot/EditSlotModal";
// import { convertTo12Hour } from "../../../utils/formatDateTime";
// import {
//   confirmAction,
//   showError,
//   showSuccess,
// } from "../../../utils/toastUtils";

// const DAYS = [
//   { label: "Sun", value: 0 },
//   { label: "Mon", value: 1 },
//   { label: "Tue", value: 2 },
//   { label: "Wed", value: 3 },
//   { label: "Thu", value: 4 },
//   { label: "Fri", value: 5 },
//   { label: "Sat", value: 6 },
// ];

// const AdminSlotManagement = () => {
//   const [slots, setSlots] = useState([]);
//   const [loading, setLoading] = useState(false);
//   const [openMenu, setOpenMenu] = useState(null);
//   const [showModal, setShowModal] = useState(false);
//   const [editingSlot, setEditingSlot] = useState(null);
//   const [dayToggleModal, setDayToggleModal] = useState(null);

//   const fetchSlots = async () => {
//     setLoading(true);
//     try {
//       const res = await axios.get(
//         `${process.env.REACT_APP_API_URL}/slot-bookings/get-all`
//       );
//       setSlots(res.data?.data || []);
//     } catch (err) {
//       console.log("failed to fetch slots", err);
//     } finally {
//       setLoading(false);
//     }
//   };

//   useEffect(() => {
//     fetchSlots();
//   }, []);

//   const toggleStatus = async (slot) => {
//     const nextStatus = slot.status === "active" ? "inactive" : "active";
//     const confirmed = await confirmAction({
//       title: `Mark slot as ${nextStatus}?`,
//       text: "This will affect future availability only.",
//       confirmText: "Yes",
//     });
//     if (!confirmed) return;

//     try {
//       await axios.patch(
//         `${process.env.REACT_APP_API_URL}/slot-bookings/status/${slot._id}`,
//         { status: nextStatus }
//       );
//       setSlots((prev) =>
//         prev.map((s) => (s._id === slot._id ? { ...s, status: nextStatus } : s))
//       );
//       showSuccess(`Slot ${nextStatus}`);
//     } catch (err) {
//       showError(err?.response?.data?.message || "Failed to update status");
//     }
//   };

//   const handleAddNew = () => {
//     setEditingSlot(null);
//     setShowModal(true);
//   };

//   const deleteSlot = async (slot) => {
//     const confirmed = await confirmAction({
//       title: "Delete this slot?",
//       text: "This will not affect existing bookings.",
//       confirmText: "Delete",
//     });
//     if (!confirmed) return;

//     try {
//       await axios.delete(
//         `${process.env.REACT_APP_API_URL}/slot-bookings/${slot._id}`
//       );
//       setSlots((prev) => prev.filter((s) => s._id !== slot._id));
//       showSuccess("Slot deleted");
//     } catch {
//       showError("Failed to delete slot");
//     }
//   };

//   return (
//     <div className="p-4">
//       <div className="w-full mx-auto">
//         <Breadcrumb
//           title1="Slots"
//           title2="Slot Templates"
//           showBtn={true}
//           buttonName={"Add New"}
//           onBtnClick={handleAddNew}
//         />

//         <div className="bg-white mt-4 rounded-lg overflow-hidden  shadow-lg ">
//           {loading ? (
//             <AdminSlotTableSkeleton rows={5} />
//           ) : slots.length === 0 ? (
//             <div className="flex flex-col items-center justify-center py-20 text-gray-400">
//               <FiClock className="text-6xl mb-4 opacity-30" />
//               <p className="text-lg font-medium">
//                 No slot templates created yet
//               </p>
//               <p className="text-sm mt-2">
//                 Click "Add Slot" to create your first time slot
//               </p>
//             </div>
//           ) : (
//             <div className="overflow-x-auto">
//               <table className="w-full table-fixed border border-gray-200 text-sm">
//                 <thead>
//                   <tr className="bg-brandAccentColor border-b-2 ">
//                     <th className="w-16 text-center  text-sm font-bold text-gray-700 uppercase tracking-wider">
//                       SL No.
//                     </th>
//                     <th className="w-60 text-center px-4 text-sm font-bold text-gray-700 uppercase tracking-wider">
//                       Time Slot
//                     </th>
//                     <th className="w-[30rem] text-center px-3 text-sm font-bold text-gray-700 uppercase tracking-wider">
//                       Active Days
//                     </th>
//                     <th className="w-52 px-6 pl-12 py-4 text-center text-sm font-bold text-gray-700 uppercase tracking-wider">
//                       Status
//                     </th>
//                     <th className="px-6 w-20 py-4 text-center text-sm font-bold text-gray-700 uppercase tracking-wider">
//                       Action
//                     </th>
//                   </tr>
//                 </thead>
//                 <tbody className="divide-y divide-gray-100">
//                   {slots.map((slot, idx) => (
//                     <tr
//                       key={slot._id}
//                       className="hover:bg-gradient-to-r hover:from-orange-50 hover:to-transparent transition-all duration-200 group"
//                     >
//                       <td className="px-6">{idx + 1}</td>
//                       {/* TIME SLOTS */}
//                       <td className="w-full px-10 tracking-wide">
//                         <div className="flex items-center gap-3">
//                           <div className="size-10 shrink-0 rounded-lg bg-gradient-to-br from-brandAccentColor to-accentTextColor flex items-center justify-center text-white font-bold shadow-md">
//                             <FiClock className="size-5" />
//                           </div>
//                           <div>
//                             <div className="text-sm font-semibold text-gray-800 whitespace-nowrap">
//                               {convertTo12Hour(slot.startTime)} –{" "}
//                               {convertTo12Hour(slot.endTime)}{" "}
//                             </div>
//                             <div className="text-xs text-gray-500">
//                               Duration:{" "}
//                               {(() => {
//                                 const [sh, sm] = slot.startTime
//                                   .split(":")
//                                   .map(Number);
//                                 const [eh, em] = slot.endTime
//                                   .split(":")
//                                   .map(Number);
//                                 const duration = eh * 60 + em - (sh * 60 + sm);
//                                 const hours = Math.floor(duration / 60);
//                                 const mins = duration % 60;
//                                 return hours > 0
//                                   ? `${hours}h ${mins}m`
//                                   : `${mins}m`;
//                               })()}
//                             </div>
//                           </div>
//                         </div>
//                       </td>

//                       {/* ACTIVE DAYS */}
//                       <td className="px-6">
//                         <div className="grid grid-cols-7 gap-2 text-center">
//                           {DAYS.map((day) => {
//                             const isActive = slot.activeDays.includes(
//                               day.value
//                             );

//                             return (
//                               <button
//                                 key={day.value}
//                                 onClick={() =>
//                                   setDayToggleModal({
//                                     slot,
//                                     day: day.value,
//                                     label: day.label,
//                                     isActive,
//                                   })
//                                 }
//                                 className={`py-2 rounded-lg text-xs font-medium transition ${
//                                   isActive
//                                     ? "bg-brandAccentColor/60 text-accentTextColor border border-[#f0653a]/40 shadow-sm"
//                                     : "bg-gray-100 text-gray-400 border border-gray-200"
//                                 } active:scale-[0.98] transform-gpu`}
//                               >
//                                 {day.label}
//                               </button>
//                             );
//                           })}
//                         </div>
//                       </td>

//                       <td className="px-6 w-full ">
//                         <button
//                           onClick={() => toggleStatus(slot)}
//                           className="group/toggle w-full  flex items-center justify-center gap-2"
//                         >
//                           {slot.status === "active" ? (
//                             <>
//                               <MdToggleOn className="text-5xl text-green-500 group-hover/toggle:text-green-600 transition-colors" />
//                               <span className="text-sm font-semibold text-green-600">
//                                 Active
//                               </span>
//                             </>
//                           ) : (
//                             <>
//                               <MdToggleOff className="text-5xl text-gray-400 group-hover/toggle:text-gray-500 transition-colors" />
//                               <span className="text-sm font-semibold text-gray-500">
//                                 Inactive
//                               </span>
//                             </>
//                           )}
//                         </button>
//                       </td>

// <td className="px-6 py-5 text-center">
//   <div className="relative inline-block">
//     <button
//       onClick={() =>
//         setOpenMenu(
//           openMenu === slot._id ? null : slot._id
//         )
//       }
//       className="p-2 rounded-lg hover:bg-gray-200 transition-colors group-hover:bg-gray-100"
//     >
//       <FiMoreVertical className="text-gray-600" />
//     </button>
//     {openMenu === slot._id && (
//       <>
//         <div
//           className="fixed inset-0 z-10"
//           onClick={() => setOpenMenu(null)}
//         />
//         {(() => {
//           const openUp = idx >= slots.length - 2;

//           return (
//             <div
//               className={`absolute right-0 w-48 bg-white rounded-xl shadow-2xl border border-gray-200 py-2 z-20 animate-in duration-200        ${
//                 openUp
//                   ? "bottom-full mb-2 slide-in-from-bottom-2"
//                   : "top-full mt-2 slide-in-from-top-2"
//               }`}
//             >
//               <button
//                 onClick={() => {
//                   setEditingSlot(slot);
//                   setShowModal(true);
//                   setOpenMenu(null);
//                 }}
//                 className="flex w-full items-center px-4 py-3 gap-3 hover:bg-orange-50 transition-colors group/item"
//               >
//                 <FiEdit className="text-[#f0653a] group-hover/item:scale-110 transition-transform" />
//                 <span className="text-gray-700 font-medium">
//                   Edit Slot
//                 </span>
//               </button>

//               <button
//                 onClick={() => {
//                   deleteSlot(slot);
//                   setOpenMenu(null);
//                 }}
//                 className="flex w-full items-center px-4 py-3 gap-3 hover:bg-red-50 transition-colors group/item"
//               >
//                 <FiTrash2 className="text-red-600 group-hover/item:scale-110 transition-transform" />
//                 <span className="text-red-600 font-medium">
//                   Delete
//                 </span>
//               </button>
//             </div>
//           );
//         })()}
//       </>
//     )}
//   </div>
// </td>
//                     </tr>
//                   ))}
//                 </tbody>
//               </table>
//             </div>
//           )}
//         </div>
//       </div>

//       {showModal && (
//         <EditSlotModal
//           slot={editingSlot}
//           onClose={() => setShowModal(false)}
//           onSuccess={() => {
//             fetchSlots();
//             setShowModal(false);
//           }}
//         />
//       )}

//       {dayToggleModal && (
//         <DayToggleModal
//           dayToggleModal={dayToggleModal}
//           fetchSlots={fetchSlots}
//           setDayToggleModal={setDayToggleModal}
//         />
//       )}
//     </div>
//   );
// };

// export default AdminSlotManagement;
